import type { Tag, TaskInstance, User } from '@hestia/core';
import { Avatar, Icon, icons, RepeatChip, TagPills, timeLabel } from '../ui.js';
import { doneLabel, fromLabel, spanLabel } from '../lang.js';

interface Props {
  instance: TaskInstance;
  users: Map<string, User>;
  tags: Map<string, Tag>;
  today: string;
  onToggle: (i: TaskInstance) => void;
  onOpen: (i: TaskInstance) => void;
  onMoveToToday?: (i: TaskInstance) => void;
  /** Which day this is, for a list that is not about one day. The day views
   *  leave it out — there, the day is the heading. */
  when?: string;
}

/** The one row component. An open task carries no colour at all — the only colour
 *  is the assignee's avatar and the list tag. A still-open task looks like every
 *  other task; it just adds a quiet "From …" tag and one gentle action. */
export function TaskRow({ instance, users, tags, today, onToggle, onOpen, onMoveToToday, when }: Props) {
  const { task, done, stillOpen } = instance;
  const span = instance.endDate;
  const assignee = task.assigneeId ? users.get(task.assigneeId) : undefined;
  const time = timeLabel(instance);

  return (
    <div className={`row${done ? ' done' : ''}${span && !done ? ' spanning' : ''}`}
         style={stillOpen ? { flexDirection: 'column', alignItems: 'stretch', gap: 13 } : undefined}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, width: '100%' }}>
        <button className={`check${done ? ' on' : ''}`} aria-pressed={done}
                aria-label={done ? 'Mark as not done' : 'Mark as done'}
                onClick={() => onToggle(instance)}>
          <Icon path={icons.check} size={12} w={3} />
        </button>

        <button style={{ flexGrow: 1, minWidth: 0, textAlign: 'left', padding: 0 }}
                onClick={() => onOpen(instance)}>
          <div className="row-title">{task.title}</div>
          <div className="row-meta">
            {done ? (
              <span className="chip" style={{ color: 'var(--ink-3)' }}>
                {doneLabel(instance, assignee?.displayName ?? null)}
              </span>
            ) : (
              <>
                {when && !span && (
                  <span className="chip" style={{ color: 'var(--ink-2)', fontWeight: 600 }}>
                    <Icon path={icons.calendar} size={13} />{when}
                  </span>
                )}
                {span && (
                  <span className="chip" style={{ color: 'var(--accent-on)', fontWeight: 600 }}>
                    <Icon path={icons.span} size={13} />{spanLabel(instance.date, span, today)}
                  </span>
                )}
                {stillOpen && !span && (
                  <span className="tag" style={{ background: 'var(--amber-tint)', color: 'var(--amber-on)' }}>
                    {fromLabel(instance.date, today)}
                  </span>
                )}
                {time && <span className="chip"><Icon path={icons.clock} size={13} />{time}</span>}
                {time && task.rrule && <span className="sep" />}
                <RepeatChip rrule={task.rrule} />
                <TagPills ids={task.tagIds} tags={tags} />
                {/* Only that there are notes. They can be an email thread; the row
                    is for scanning, and opening the task is one tap away. */}
                {task.notes && (
                  <span className="chip" title="Has notes" aria-label="Has notes">
                    <Icon path={icons.note} size={13} />
                  </span>
                )}
              </>
            )}
          </div>
        </button>

        <Avatar user={assignee} muted={done} size={30} />
      </div>

      {stillOpen && onMoveToToday && (
        <button className="pill pill-soft" style={{ height: 38, fontSize: 13.5 }}
                onClick={() => onMoveToToday(instance)}>
          <Icon path={icons.arrowRight} size={15} />Move to today
        </button>
      )}
    </div>
  );
}
